import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildRestUrlWithQuery, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IFastbound_Account, { INewFastbound_Account } from "../models/IFastbound_Account";
import IFastbound_Queue from "../models/IFastbound_Queue";
import IFastbound_Error_Log from "../models/IFastbound_Error_Log";
import { FastBoundErrorType } from "../models/INewFastbound_Error_Log";
import ISerial from "../models/ISerial";
import IFastBoundPendingSerialsQuery from "../models/DTO/IFastBoundPendingSerialsQuery";
import IAssignFastBoundSerialDTO from "../models/DTO/IAssignFastBoundSerialDTO";
import IBulkAssignFastBoundSerialsDTO from "../models/DTO/IBulkAssignFastBoundSerialsDTO";
import { IFastBoundError, IFastBoundDispositionDetail } from "../models/IFastBound";

export class FastBoundValidationError extends Error {
    errors: IFastBoundError[];
    constructor(message: string, errors: IFastBoundError[]) {
        super(message);
        this.name = "FastBoundValidationError";
        this.errors = errors;
    }
}

// Response interfaces

interface FastBoundTestConnectionResponse {
    success: boolean;
    account?: any;
}

interface FastBoundConfigUpdateResponse {
    success: boolean;
    account: IFastbound_Account;
}

interface FastBoundDashboardResponse {
    accounts: IFastbound_Account[];
    activeQueueItems: IFastbound_Queue[];
    recentQueueItems: IFastbound_Queue[];
    errorCount: number;
}

interface FastBoundQueueStatusResponse {
    activeCount: number;
    pendingItems: IFastbound_Queue[];
    processingItems: IFastbound_Queue[];
}

interface FastBoundSyncResponse {
    success: boolean;
    message: string;
    queue_id: number;
    guid: string;
}

interface FastBoundErrorLogsResponse {
    rows: IFastbound_Error_Log[];
    count: number;
}

interface FastBoundDismissAllResponse {
    success: boolean;
    dismissed_count: number;
}

interface FastBoundRetryResponse {
    success: boolean;
    message: string;
    queue_id: number;
}

interface FastBoundPendingSerialsResponse {
    rows: ISerial[];
    count: number;
}

interface FastBoundBulkOperationResponse {
    assigned?: number;
    dismissed?: number;
    restored?: number;
    notFound: number[];
}

// Export response types for use in components
export type {
    FastBoundTestConnectionResponse,
    FastBoundConfigUpdateResponse,
    FastBoundDashboardResponse,
    FastBoundQueueStatusResponse,
    FastBoundSyncResponse,
    FastBoundErrorLogsResponse,
    FastBoundDismissAllResponse,
    FastBoundRetryResponse,
    FastBoundPendingSerialsResponse,
    FastBoundBulkOperationResponse,
    FastBoundContactSearchField,
};

// Account CRUD

const getAccounts = async (userModel: IUserUI, supplierModel: ISupplierModel): Promise<IFastbound_Account[]> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundGetAccounts });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
        throw new Error(`Failed to get FastBound accounts: ${response.statusText}`);
    }
    return response.json();
};

const createAccount = async (
    body: INewFastbound_Account & { api_key?: string; account_number?: string; is_enabled?: boolean; audit_user_email?: string },
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IFastbound_Account> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundCreateAccount });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error(`Failed to create FastBound account: ${response.statusText}`);
    }
    return response.json();
};

const getAccountById = async (accountId: number, userModel: IUserUI, supplierModel: ISupplierModel): Promise<IFastbound_Account> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.FastBoundGetAccountById,
        params: { accountId: String(accountId) },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
        throw new Error(`Failed to get FastBound account: ${response.statusText}`);
    }
    return response.json();
};

const updateAccount = async (
    accountId: number,
    body: Partial<INewFastbound_Account> & { api_key?: string; account_number?: string; is_enabled?: boolean; audit_user_email?: string },
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IFastbound_Account> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.FastBoundUpdateAccount,
        params: { accountId: String(accountId) },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "PUT",
        headers,
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error(`Failed to update FastBound account: ${response.statusText}`);
    }
    return response.json();
};

const deleteAccount = async (accountId: number, userModel: IUserUI, supplierModel: ISupplierModel): Promise<{ success: boolean }> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.FastBoundDeleteAccount,
        params: { accountId: String(accountId) },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "DELETE", headers });
    if (!response.ok) {
        throw new Error(`Failed to delete FastBound account: ${response.statusText}`);
    }
    return response.json();
};

const testAccountConnection = async (
    accountId: number,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<FastBoundTestConnectionResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.FastBoundTestAccountConnection,
        params: { accountId: String(accountId) },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "POST", headers });
    if (!response.ok) {
        throw new Error(`Failed to test FastBound connection: ${response.statusText}`);
    }
    return response.json();
};

const testConnection = async (
    api_key: string,
    account_number: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<FastBoundTestConnectionResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundTestConnection });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ api_key, account_number }),
    });
    if (!response.ok) {
        throw new Error(`Failed to test FastBound connection: ${response.statusText}`);
    }
    return response.json();
};

// Config

const getConfig = async (userModel: IUserUI, supplierModel: ISupplierModel): Promise<IFastbound_Account[]> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundGetConfig });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
        throw new Error(`Failed to get FastBound config: ${response.statusText}`);
    }
    return response.json();
};

const updateConfig = async (
    body: {
        fastbound_account_id: number;
        disposition_workflow_step_id?: number | null;
        disposition_type?: string | null;
        disposition_category?: string | null;
        nfa_disposition_type?: string | null;
        acquisition_type_filter?: string | null;
        serial_format_pattern?: string | null;
        auto_sync_acquisitions?: boolean;
        auto_sync_contacts?: boolean;
        disposition_hold_pending?: boolean;
    },
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<FastBoundConfigUpdateResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundUpdateConfig });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "PUT",
        headers,
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error(`Failed to update FastBound config: ${response.statusText}`);
    }
    return response.json();
};

// Dashboard

const getDashboard = async (
    fastbound_account_id?: number,
    userModel?: IUserUI,
    supplierModel?: ISupplierModel
): Promise<FastBoundDashboardResponse> => {
    const query: Record<string, any> = {};
    if (fastbound_account_id) {
        query.fastbound_account_id = fastbound_account_id;
    }

    const url = buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.FastBoundGetDashboard, query });
    const headers = buildAuthHeadersForFetch({ userModel: userModel!, supplierModel: supplierModel! });

    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
        throw new Error(`Failed to get FastBound dashboard: ${response.statusText}`);
    }
    return response.json();
};

// Queue

const getQueueStatus = async (
    fastbound_account_id?: number,
    userModel?: IUserUI,
    supplierModel?: ISupplierModel
): Promise<FastBoundQueueStatusResponse> => {
    const query: Record<string, any> = {};
    if (fastbound_account_id) {
        query.fastbound_account_id = fastbound_account_id;
    }

    const url = buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.FastBoundGetQueueStatus, query });
    const headers = buildAuthHeadersForFetch({ userModel: userModel!, supplierModel: supplierModel! });

    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
        throw new Error(`Failed to get FastBound queue status: ${response.statusText}`);
    }
    return response.json();
};

// Sync

const syncAcquisitions = async (
    fastbound_account_id: number,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<FastBoundSyncResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundSyncAcquisitions });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ fastbound_account_id }),
    });
    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const err: any = new Error(errorBody.message || `Failed to sync acquisitions: ${response.statusText}`);
        err.description = errorBody.description;
        throw err;
    }
    return response.json();
};

const syncContacts = async (
    fastbound_account_id: number,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<FastBoundSyncResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundSyncContacts });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ fastbound_account_id }),
    });
    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const err: any = new Error(errorBody.message || `Failed to sync contacts: ${response.statusText}`);
        err.description = errorBody.description;
        throw err;
    }
    return response.json();
};

const syncItems = async (
    fastbound_account_id: number,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<FastBoundSyncResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundSyncItems });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ fastbound_account_id }),
    });
    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const err: any = new Error(errorBody.message || `Failed to sync items: ${response.statusText}`);
        err.description = errorBody.description;
        throw err;
    }
    return response.json();
};

// Error Logs

const getErrorLogs = async (
    params: {
        page?: number;
        pageSize?: number;
        type?: FastBoundErrorType;
        is_dismissed?: boolean;
        fastbound_account_id?: number;
    },
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<FastBoundErrorLogsResponse> => {
    const query: Record<string, any> = {};
    if (params.page) query.page = params.page;
    if (params.pageSize) query.pageSize = params.pageSize;
    if (params.type) query.type = params.type;
    if (params.is_dismissed !== undefined) query.is_dismissed = params.is_dismissed;
    if (params.fastbound_account_id) query.fastbound_account_id = params.fastbound_account_id;

    const url = buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.FastBoundGetErrorLogs, query });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
        throw new Error(`Failed to get FastBound error logs: ${response.statusText}`);
    }
    return response.json();
};

const getErrorLogById = async (errorId: number, userModel: IUserUI, supplierModel: ISupplierModel): Promise<IFastbound_Error_Log> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.FastBoundGetErrorLogById,
        params: { errorId: String(errorId) },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
        throw new Error(`Failed to get FastBound error log: ${response.statusText}`);
    }
    return response.json();
};

const dismissErrorLog = async (errorId: number, userModel: IUserUI, supplierModel: ISupplierModel): Promise<{ success: boolean }> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.FastBoundDismissErrorLog,
        params: { errorId: String(errorId) },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "POST", headers });
    if (!response.ok) {
        throw new Error(`Failed to dismiss FastBound error: ${response.statusText}`);
    }
    return response.json();
};

const dismissAllErrorLogs = async (
    fastbound_account_id?: number,
    userModel?: IUserUI,
    supplierModel?: ISupplierModel
): Promise<FastBoundDismissAllResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundDismissAllErrorLogs });
    const headers = buildAuthHeadersForFetch({ userModel: userModel!, supplierModel: supplierModel! });

    const body: Record<string, any> = {};
    if (fastbound_account_id) {
        body.fastbound_account_id = fastbound_account_id;
    }

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error(`Failed to dismiss all FastBound errors: ${response.statusText}`);
    }
    return response.json();
};

const retryErrorLog = async (errorId: number, userModel: IUserUI, supplierModel: ISupplierModel): Promise<FastBoundRetryResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.FastBoundRetryErrorLog,
        params: { errorId: String(errorId) },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "POST", headers });
    if (!response.ok) {
        throw new Error(`Failed to retry FastBound error: ${response.statusText}`);
    }
    return response.json();
};

// Queue Operations

const retryQueueItem = async (queueId: number, userModel: IUserUI, supplierModel: ISupplierModel): Promise<FastBoundRetryResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.FastBoundRetryQueueItem,
        params: { queueId: String(queueId) },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "POST", headers });
    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const err: any = new Error(errorBody.message || `Failed to retry queue item: ${response.statusText}`);
        err.description = errorBody.description;
        throw err;
    }
    return response.json();
};

// Pending Serials

const getPendingSerials = async (
    query: IFastBoundPendingSerialsQuery,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<FastBoundPendingSerialsResponse> => {
    const url = buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.FastBoundGetPendingSerials, query });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
        throw new Error(`Failed to get pending serials: ${response.statusText}`);
    }
    return response.json();
};

const assignSerial = async (
    serialId: number,
    body: IAssignFastBoundSerialDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<ISerial> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.FastBoundAssignSerial,
        params: { serialId: String(serialId) },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "PUT",
        headers,
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error(`Failed to assign serial: ${response.statusText}`);
    }
    return response.json();
};

const bulkAssignSerials = async (
    body: IBulkAssignFastBoundSerialsDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<FastBoundBulkOperationResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundBulkAssignSerials });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error(`Failed to bulk assign serials: ${response.statusText}`);
    }
    return response.json();
};

const dismissSerials = async (
    serialIds: number[],
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<FastBoundBulkOperationResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundDismissSerials });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ serialIds }),
    });
    if (!response.ok) {
        throw new Error(`Failed to dismiss serials: ${response.statusText}`);
    }
    return response.json();
};

const undismissSerials = async (
    serialIds: number[],
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<FastBoundBulkOperationResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundUndismissSerials });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ serialIds }),
    });
    if (!response.ok) {
        throw new Error(`Failed to undismiss serials: ${response.statusText}`);
    }
    return response.json();
};

type FastBoundContactSearchField = "licenseName" | "fflNumber" | "organizationName" | "firstName" | "lastName";

const searchContacts = async (
    search: string,
    fastbound_account_id: number,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    searchField?: FastBoundContactSearchField
): Promise<any[]> => {
    const query: Record<string, string | number> = { fastbound_account_id };
    if (search) {
        const field = searchField || "licenseName";
        query[field] = search;
    }

    const url = buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.FastBoundGetContacts, query });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
        throw new Error(`Failed to search FastBound contacts: ${response.statusText}`);
    }
    const data = await response.json();
    return data.contacts || data;
};

const createDisposition = async (
    body: {
        fastbound_account_id: number;
        order_id: number;
    },
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<{ success: boolean }> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundCreateAndCommitDisposition });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        const body = await response.json().catch(() => null);
        if (body?.errors?.length) {
            throw new FastBoundValidationError(body.message || "FastBound validation error", body.errors);
        }
        throw new Error(body?.message || `Failed to create disposition: ${response.statusText}`);
    }
    const result = await response.json();
    if (!result.success && result.errors?.length) {
        throw new FastBoundValidationError(result.message || "FastBound validation error", result.errors);
    }
    return result;
};

const createDispositionForOrder = async (
    body: { order_id: number; date?: string },
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<{ dispositionIds: string[] }> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FastBoundCreateDispositionForOrder });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        const body = await response.json().catch(() => null);
        if (body?.errors?.length) {
            throw new FastBoundValidationError(body.message || "FastBound validation error", body.errors);
        }
        throw new Error(body?.message || `Failed to create disposition: ${response.statusText}`);
    }
    const result = await response.json();
    if (result.success === false && result.errors?.length) {
        throw new FastBoundValidationError(result.message || "FastBound validation error", result.errors);
    }
    return result;
};

const getDispositionById = async (
    dispositionId: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IFastBoundDispositionDetail> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.FastBoundGetDispositionById,
        params: { id: String(dispositionId) },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
        throw new Error(`Failed to get disposition: ${response.statusText}`);
    }
    return response.json();
};

const FastBoundService = {
    getAccounts,
    createAccount,
    getAccountById,
    updateAccount,
    deleteAccount,
    testAccountConnection,
    testConnection,
    getConfig,
    updateConfig,
    getDashboard,
    getQueueStatus,
    syncAcquisitions,
    syncContacts,
    syncItems,
    getErrorLogs,
    getErrorLogById,
    dismissErrorLog,
    dismissAllErrorLogs,
    retryErrorLog,
    retryQueueItem,
    getPendingSerials,
    assignSerial,
    bulkAssignSerials,
    dismissSerials,
    undismissSerials,
    searchContacts,
    createDisposition,
    createDispositionForOrder,
    getDispositionById,
};

export default FastBoundService;
